import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button
} from '@mui/material';
import {
  DataObject as DataObjectIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
  ArrowForwardIos
} from '@mui/icons-material';
import { Handle } from 'react-flow-renderer';
import { useNodeStorage } from '../../../stores/useNodeStorage';

const VariableNode = React.memo(({ data, id, isConnectable }) => {
  const [variableName, setVariableName] = useState(data?.variableName || '');
  const [variableValue, setVariableValue] = useState(data?.variableValue || '');
  const [savedStatus, setSavedStatus] = useState(data?.savedStatus || '');
  const [showSavePopup, setShowSavePopup] = useState(false);

  const storageItems = useNodeStorage();

  // Sincronizar estado local se data mudar externamente (ex: duplicação ou carregamento)
  useEffect(() => {
    if (data?.variableName !== undefined && data.variableName !== variableName) {
      setVariableName(data.variableName);
    }
    if (data?.variableValue !== undefined && data.variableValue !== variableValue) {
      setVariableValue(data.variableValue);
    }
  }, [data?.variableName, data?.variableValue]);

  // Atualizar data de forma segura quando o estado mudar
  useEffect(() => {
    if (data) {
      data.variableName = variableName;
      data.variableValue = variableValue;
      data.savedStatus = savedStatus;
    }
  }, [variableName, variableValue, savedStatus, data]);

  useEffect(() => {
    let timer;
    if (showSavePopup) {
      timer = setTimeout(() => {
        setShowSavePopup(false);
      }, 2500);
    }
    return () => clearTimeout(timer);
  }, [showSavePopup]);

  const handleSave = useCallback(() => {
    const trimmedName = (variableName || '').trim();
    if (trimmedName) {
      window.flowVariables = window.flowVariables || {};
      window.flowVariables[trimmedName] = variableValue;

      // Sincronizar com localStorage para os outros nós e modais
      try {
        const local = JSON.parse(localStorage.getItem('variables') || '[]');
        if (Array.isArray(local) && !local.includes(trimmedName)) {
          local.push(trimmedName);
          localStorage.setItem('variables', JSON.stringify(local));
        }
      } catch (e) {
        console.warn('Erro ao atualizar localStorage variables:', e);
      }

      // Disparar evento para outros componentes
      const event = new CustomEvent('flowVariableUpdate', {
        detail: { name: trimmedName, value: variableValue }
      });
      window.dispatchEvent(event);

      setSavedStatus('Salvo!');
      setShowSavePopup(true);
    }
  }, [variableName, variableValue]);

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: 2,
        boxShadow: 'rgba(0, 0, 0, 0.08) 0px 3px 6px',
        border: '1px solid #0284c7',
        p: 2,
        width: 280,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        position: 'relative',
      }}
    >
      {/* Handle de Entrada (target) padronizado */}
      <Handle
        type="target"
        position="left"
        isConnectable={isConnectable}
        style={{
          background: '#0284c7',
          width: '18px',
          height: '18px',
          top: '20px',
          left: '-12px',
          cursor: 'pointer'
        }}
        onConnect={params => console.log('handle onConnect', params)}
      >
        <ArrowForwardIos
          sx={{
            color: '#ffffff',
            width: '10px',
            height: '10px',
            marginLeft: '2.9px',
            marginBottom: '1px',
            pointerEvents: 'none'
          }}
        />
      </Handle>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DataObjectIcon fontSize="small" sx={{ color: '#0284c7' }} />
          <Typography variant="subtitle2" fontWeight="bold" color="#0369a1">
            Variável Global
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              storageItems.setNodesStorage(id);
              storageItems.setAct('duplicate');
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ color: '#0284c7' }} />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => {
              storageItems.setNodesStorage(id);
              storageItems.setAct('delete');
            }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ color: '#0284c7' }} />
          </IconButton>
        </Box>
      </Box>

      <TextField
        label="Nome da variável"
        size="small"
        placeholder="Ex: cpf, pedido, resposta"
        value={variableName}
        onChange={(e) => {
          const val = e.target.value;
          setVariableName(val);
          if (data) data.variableName = val;
        }}
        fullWidth
      />

      <TextField
        label="Valor"
        size="small"
        placeholder={'Valor estático ou ${outraVariavel}'}
        value={variableValue}
        onChange={(e) => {
          const val = e.target.value;
          setVariableValue(val);
          if (data) data.variableValue = val;
        }}
        fullWidth
        multiline
        minRows={2}
        maxRows={4}
        helperText={'Referencie no fluxo usando ${nome}'}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
        <Typography variant="caption" color={savedStatus === 'Salvo!' ? 'success.main' : 'text.secondary'}>
          {savedStatus}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          startIcon={<CheckCircleOutlineIcon />}
          sx={{
            textTransform: 'none',
            backgroundColor: '#0284c7',
            '&:hover': { backgroundColor: '#0369a1' },
            boxShadow: 1
          }}
        >
          Salvar
        </Button>
      </Box>

      {showSavePopup && (
        <Box
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: '#16a34a',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 1000,
            fontSize: '11px',
            fontWeight: 600
          }}
        >
          Salvo com sucesso!
        </Box>
      )}

      {/* Handle de Saída (source) padronizado com id="a" */}
      <Handle
        type="source"
        position="right"
        id="a"
        isConnectable={isConnectable}
        style={{
          background: '#0284c7',
          width: '18px',
          height: '18px',
          top: '70%',
          right: '-11px',
          cursor: 'pointer'
        }}
      >
        <ArrowForwardIos
          sx={{
            color: '#ffffff',
            width: '10px',
            height: '10px',
            marginLeft: '2.9px',
            marginBottom: '1px',
            pointerEvents: 'none'
          }}
        />
      </Handle>
    </Box>
  );
});

export default VariableNode;

export const getFlowVariable = (name) => {
  if (!window.flowVariables) {
    return undefined;
  }
  return window.flowVariables[name];
};

export const setFlowVariable = (name, value) => {
  if (!window.flowVariables) {
    window.flowVariables = {};
  }
  window.flowVariables[name] = value;
  const event = new CustomEvent('flowVariableUpdate', {
    detail: { name, value }
  });
  window.dispatchEvent(event);
  return value;
};